import React, { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

// style components
import ButtonCardsContainer, {
    ButtonCardDescription,
    ButtonCardIcon,
    ButtonCardTitle,
    HourlyDisabledWrapper
} from '../../style/ButtonCardsContainer.style'

// components
import ButtonCard from '../../../../components/ButtonCard/ButtonCard.component'
import { ActiveFeature, LotDurationType, LotViewName } from '../../../../types/lot.interface'
import { getNightParkingTimesFromConfigMemoized } from '../../../../utils/getNightParkingTimesFromConfig.utils'

interface ButtonCardsSectionProps {
    hourlyDisabled: boolean
    activeFeatures?: ActiveFeature[]
    onCardClick: (type: LotDurationType) => void
}

const ButtonCardsSection: FC<ButtonCardsSectionProps> = ({ hourlyDisabled, activeFeatures, onCardClick }) => {
    const { t } = useTranslation()
    const [showHourlyDisabledText, setShowHourlyDisabledText] = useState(false)
    const [nightParkingTimes, setNightParkingTimes] = useState<{ start_time: string; end_time: string }>()

    const onHourlyClick = () => {
        if (hourlyDisabled) {
            setShowHourlyDisabledText(true)
            return
        }
        onCardClick(LotDurationType.SHORT)
    }

    const isFeatureEnabled = (featureName: string) => {
        return activeFeatures?.find((feature) => feature.name === featureName)?.enabled
    }

    useEffect(() => {
        if (activeFeatures) {
            const feature = activeFeatures.find((f) => f.name.toLocaleLowerCase() === LotViewName.NIGHTLY.toLowerCase())
            if (!feature) return
            const { start, end } = getNightParkingTimesFromConfigMemoized(feature)
            setNightParkingTimes({ start_time: start, end_time: end })
        }
    }, [activeFeatures])

    return (
        <ButtonCardsContainer>
            {isFeatureEnabled(LotViewName.HOURLY.toLowerCase()) && (
                <ButtonCard disabled={hourlyDisabled} onClick={onHourlyClick}>
                    <ButtonCardIcon data-testid="hourly-card" className="wemolo-ticket-1" aria-hidden="true" />
                    <ButtonCardTitle>{t('lot.hourly')}</ButtonCardTitle>
                    <ButtonCardDescription>{t('lot.hourlyButtonDesc')}</ButtonCardDescription>
                </ButtonCard>
            )}
            {showHourlyDisabledText && <HourlyDisabledWrapper>{t('lot.cantPark')}</HourlyDisabledWrapper>}

            {isFeatureEnabled(LotViewName.DAILY.toLowerCase()) && (
                <ButtonCard onClick={() => onCardClick(LotDurationType.LONG)}>
                    <ButtonCardIcon data-testid="daily-card" className="wemolo-clock" aria-hidden="true" />
                    <ButtonCardTitle>{t('lot.daily')}</ButtonCardTitle>
                    <ButtonCardDescription>{t('lot.dailyButtonDesc')}</ButtonCardDescription>
                </ButtonCard>
            )}

            {isFeatureEnabled(LotViewName.SUBSCRIPTION.toLowerCase()) && (
                <ButtonCard onClick={() => onCardClick(LotDurationType.SUBSCRIPTION)}>
                    <ButtonCardIcon className="wemolo-subscription" aria-hidden="true" />
                    <ButtonCardTitle>{t('lot.subscription')}</ButtonCardTitle>
                    <ButtonCardDescription>{t('lot.subscriptionButtonDesc')}</ButtonCardDescription>
                </ButtonCard>
            )}

            {isFeatureEnabled(LotViewName.NIGHTLY.toLowerCase()) && (
                <ButtonCard onClick={() => onCardClick(LotDurationType.NIGHT)}>
                    <ButtonCardIcon data-testid="nightly-card" className="pkd-night-parking" aria-hidden="true" />
                    <ButtonCardTitle>{t('lot.nightParking')}</ButtonCardTitle>
                    <ButtonCardDescription>
                        {t('lot.nightlyButtonDesc', {
                            start_time: nightParkingTimes?.start_time,
                            end_time: nightParkingTimes?.end_time
                        })}
                    </ButtonCardDescription>
                </ButtonCard>
            )}
        </ButtonCardsContainer>
    )
}

export default React.memo(ButtonCardsSection)
